import { useState, useCallback } from "react";

/**
 * Hook for managing authentication state
 * @returns {Object} Authentication state and methods
 */
export const useAuth = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const login = useCallback(async (credentials) => {
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const user = {
        id: Date.now(),
        username: credentials.username,
        email: credentials.email || `${credentials.username}@example.com`,
        avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${credentials.username}`,
        joinedAt: new Date().toISOString(),
      };

      setCurrentUser(user);
      setIsAuthenticated(true);

      // Persist to localStorage
      localStorage.setItem("gg_user", JSON.stringify(user));

      // Notify app about auth change (same-tab listeners)
      try {
        window.dispatchEvent(
          new CustomEvent("gg:auth-changed", { detail: { user } })
        );
      } catch {
        // Ignore event dispatch errors
      }

      return { success: true, user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const logout = useCallback(() => {
    setCurrentUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem("gg_user");
    try {
      window.dispatchEvent(
        new CustomEvent("gg:auth-changed", { detail: { user: null } })
      );
    } catch {
      // Ignore event dispatch errors
    }
  }, []);

  const loadPersistedUser = useCallback(() => {
    try {
      const persisted = localStorage.getItem("gg_user");
      if (persisted) {
        const user = JSON.parse(persisted);
        setCurrentUser(user);
        setIsAuthenticated(true);
        return user;
      }
    } catch (error) {
      console.warn("Failed to load persisted user:", error);
    }
    return null;
  }, []);

  return {
    currentUser,
    isAuthenticated,
    login,
    logout,
    loadPersistedUser,
  };
};
